/**
 * GraphQL Utilities
 * 
 * Shared utilities for GraphQL error handling and response processing.
 * Use these instead of duplicating error handling in every mutation hook.
 */

import type { CombinedError } from "urql";
import { sanitizeErrorMessage } from "./graphql";

// =============================================================================
// ERROR EXTRACTION
// =============================================================================

/**
 * Extract the most user-friendly error message from a GraphQL error.
 * Prioritizes GraphQL errors over network errors.
 * All messages are sanitized to prevent showing technical details to users.
 * 
 * @param error - URQL CombinedError
 * @param defaultMessage - Fallback message if no specific error found
 * @returns User-friendly error message
 * 
 * @example
 * ```ts
 * const message = extractGraphQLErrorMessage(response.error, "Не удалось сохранить");
 * ```
 */
export function extractGraphQLErrorMessage(
  error: CombinedError | undefined,
  defaultMessage: string
): string {
  if (!error) return defaultMessage;
  
  // Check for network errors first (return user-friendly message)
  if (error.networkError) {
    return "Ошибка сети. Проверьте подключение к интернету.";
  }
  
  // Prioritize GraphQL errors (they're usually more specific)
  if (error.graphQLErrors.length > 0) {
    const firstError = error.graphQLErrors[0];
    // Sanitize the GraphQL error message
    return sanitizeErrorMessage(firstError.message, defaultMessage);
  }
  
  // Fall back to generic error message (sanitized)
  return sanitizeErrorMessage(error.message, defaultMessage);
}

/**
 * Throw an error with a user-friendly message extracted from GraphQL response.
 * Use in mutation hooks when you need to throw and let the caller handle the error.
 * 
 * @param error - URQL CombinedError
 * @param defaultMessage - Fallback message
 * @throws Error with the extracted message
 * 
 * @example
 * ```ts
 * if (response.error) {
 *   throwGraphQLError(response.error, "Не удалось создать заказ");
 * }
 * ```
 */
export function throwGraphQLError(
  error: CombinedError | undefined,
  defaultMessage: string
): never {
  throw new Error(extractGraphQLErrorMessage(error, defaultMessage));
}

// =============================================================================
// ERROR CODE EXTRACTION
// =============================================================================

/**
 * Extract error code from GraphQL error extensions.
 * Useful for handling specific error types differently.
 * 
 * @param error - URQL CombinedError
 * @returns Error code or null if not found
 * 
 * @example
 * ```ts
 * const code = getGraphQLErrorCode(response.error);
 * if (code === "DUPLICATE_ORDER") {
 *   // Handle duplicate order specifically
 * }
 * ```
 */
export function getGraphQLErrorCode(error: CombinedError | undefined): string | null {
  if (!error?.graphQLErrors.length) return null;
  
  const firstError = error.graphQLErrors[0];
  const code = firstError.extensions?.code;
  
  return typeof code === "string" ? code : null;
}

// =============================================================================
// ERROR TYPE CHECKS
// =============================================================================

/**
 * Check if error is an authentication error (401/UNAUTHENTICATED).
 * 
 * @param error - URQL CombinedError
 * @returns True if this is an auth error
 */
export function isAuthenticationError(error: CombinedError | undefined): boolean {
  if (!error) return false;
  
  // Check for GraphQL UNAUTHENTICATED code
  if (error.graphQLErrors.some(e => 
    e.extensions?.code === "UNAUTHENTICATED" ||
    e.message?.toLowerCase().includes("authentication required")
  )) {
    return true;
  }
  
  // Check for 401 status in response
  if (error.response?.status === 401) {
    return true;
  }
  
  return false;
}

/**
 * Check if error is a validation error (invalid input).
 * 
 * @param error - URQL CombinedError
 * @returns True if this is a validation error
 */
export function isValidationError(error: CombinedError | undefined): boolean {
  if (!error) return false;
  
  return error.graphQLErrors.some(e =>
    e.extensions?.code === "BAD_USER_INPUT" ||
    e.extensions?.code === "VALIDATION_ERROR"
  );
}

/**
 * Check if error is a network error (fetch failed, timeout, etc.).
 * 
 * @param error - URQL CombinedError
 * @returns True if this is a network error
 */
export function isNetworkError(error: CombinedError | undefined): boolean {
  if (!error) return false;
  
  if (error.networkError) return true;
  
  const msg = error.message?.toLowerCase() ?? "";
  return (
    msg.includes("network") ||
    msg.includes("fetch") ||
    msg.includes("timeout") ||
    msg.includes("failed to fetch")
  );
}

// =============================================================================
// RESPONSE HELPERS
// =============================================================================

/**
 * Check if a mutation response has an error.
 * Simplifies the common pattern of checking for errors after mutation.
 * 
 * @param response - URQL mutation response
 * @returns True if there's an error
 * 
 * @example
 * ```ts
 * const response = await executeMutation({ id });
 * if (hasResponseError(response)) {
 *   const message = extractGraphQLErrorMessage(response.error, "Ошибка");
 *   showError(message);
 *   return;
 * }
 * // response.data is safe to use
 * ```
 */
export function hasResponseError<T>(
  response: { data?: T; error?: CombinedError }
): response is { data?: T; error: CombinedError } {
  return !!response.error;
}

/**
 * Assert that mutation response has data (no error).
 * Throws if there's an error, making TypeScript narrow the type.
 * 
 * @param response - URQL mutation response
 * @param defaultMessage - Error message if no data
 * @returns The response data (guaranteed to exist)
 * 
 * @example
 * ```ts
 * const response = await executeMutation({ id });
 * const data = assertResponseData(response, "Не удалось создать");
 * // data is now guaranteed to be non-null
 * ```
 */
export function assertResponseData<T>(
  response: { data?: T; error?: CombinedError },
  defaultMessage: string
): T {
  if (response.error) {
    throwGraphQLError(response.error, defaultMessage);
  }
  
  if (!response.data) {
    throw new Error(defaultMessage);
  }
  
  return response.data;
}
